import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell,
  MagnifyingGlass,
  CheckSquare,
  Gavel,
  ChatTeardropDots,
  CurrencyGbp,
  ClipboardText,
  Warning,
  X,
  ArrowsClockwise,
  CheckCircle,
  Users,
  CalendarCheck,
  Clock,
  FileText,
  Briefcase,
  Scales,
  XCircle,
} from '@phosphor-icons/react';
import { useAuthStore } from '@/features/auth/store';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import {
  fetchMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  dismissNotification,
  type ServerNotification,
} from '@/lib/notificationsApi';
import { resolveNotificationLink } from '@/lib/notificationRouting';
import type { Notification as StoreNotification, NotifType } from '@/lib/notificationsStore';
import { cn } from '@/lib/utils';

// ─── Icon map (mirrors TopBar) ──────────────────────────────────
const ICON: Record<string, { Icon: React.ElementType; tone: string }> = {
  message_received:         { Icon: ChatTeardropDots, tone: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  brief_received:           { Icon: Briefcase,        tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  brief_accepted:           { Icon: CheckCircle,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  brief_returned:           { Icon: ArrowsClockwise,  tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  brief_declined:           { Icon: ArrowsClockwise,  tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  brief_withdrawn:          { Icon: ArrowsClockwise,  tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  conference_requested:     { Icon: Users,            tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  conference_scheduled:     { Icon: CalendarCheck,    tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  conference_cancelled:     { Icon: XCircle,          tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  conference_completed:     { Icon: CheckCircle,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  hearing_booked:           { Icon: Gavel,            tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  hearing_assigned:         { Icon: Gavel,            tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  hearing_reminder:         { Icon: Clock,            tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  hearing_outcome_pending:  { Icon: Warning,          tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  case_assigned:            { Icon: Scales,           tone: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  matter_inactive:          { Icon: Clock,            tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  conflict_check_required:  { Icon: Warning,          tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  task_assigned:            { Icon: ClipboardText,    tone: 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/15' },
  task_due:                 { Icon: Clock,            tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  document_uploaded:        { Icon: FileText,         tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' },
  fee_note_issued:          { Icon: CurrencyGbp,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  fee_note_paid:            { Icon: CurrencyGbp,      tone: 'text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-500/15' },
  fee_note_pending:         { Icon: CurrencyGbp,      tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  kyc_expiring:             { Icon: Warning,          tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  limitation_approaching:   { Icon: Warning,          tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
  judgment_reserved:        { Icon: Gavel,            tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  clerk_coordination_requested: { Icon: Users,        tone: 'text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/15' },
  clerk_coordination_updated:   { Icon: CalendarCheck, tone: 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/15' },
  clerk_coordination_cancelled: { Icon: XCircle,      tone: 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/15' },
};

const PRIORITY_LABEL: Record<string, string> = {
  critical: 'Critical',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)} d ago`;
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Filter chip ────────────────────────────────────────────────
function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-full border px-3 py-1 text-xs font-medium transition',
        active
          ? 'border-brand-600 bg-brand-600 text-white'
          : 'border-border text-muted-foreground hover:border-brand-400 hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

// ─── Page ───────────────────────────────────────────────────────
export default function NotificationsPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [category, setCategory] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  const queryKey = ['notifications-centre', unreadOnly, category, priorityFilter];

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    queryKey,
    queryFn: () =>
      fetchMyNotifications({
        limit: 100,
        unreadOnly,
        category: category || undefined,
        priority: priorityFilter || undefined,
      }),
    refetchOnWindowFocus: true,
  });

  const total = data?.total ?? 0;
  const all = data?.notifications ?? [];

  // Client-side search across already-fetched results
  const filtered = useMemo(() => {
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (n) =>
        n.title.toLowerCase().includes(q) ||
        (n.body ?? '').toLowerCase().includes(q),
    );
  }, [all, search]);

  const unreadCount = filtered.filter((n) => !n.readAt).length;
  const criticalCount = filtered.filter((n) => n.priority === 'critical' && !n.readAt).length;

  function handleOpen(n: ServerNotification) {
    void markNotificationRead(n.id).catch(() => undefined);
    qc.invalidateQueries({ queryKey });
    qc.invalidateQueries({ queryKey: ['notifications'] });
    qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    const routingProxy: StoreNotification = {
      id: n.id,
      forRole: 'clerk',
      fromName: '',
      fromRole: 'admin',
      type: n.type as NotifType,
      priority: n.priority,
      title: n.title,
      detail: n.body ?? '',
      link: n.link ?? undefined,
      entityType: n.entityType ?? undefined,
      entityId: n.entityId ?? undefined,
      read: Boolean(n.readAt),
      createdAt: new Date(n.createdAt).getTime(),
    };
    const target = resolveNotificationLink(routingProxy, user!);
    if (target) navigate(target);
  }

  function handleDismiss(id: string) {
    void dismissNotification(id)
      .then(() => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: ['notifications'] });
        qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      })
      .catch(() => undefined);
  }

  function handleMarkAllRead() {
    void markAllNotificationsRead()
      .then(() => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: ['notifications'] });
        qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
      })
      .catch(() => undefined);
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      {/* Page header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Notifications</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {isFetching ? 'Refreshing…' : `${total} total${unreadCount > 0 ? ` · ${unreadCount} unread` : ''}`}
            {criticalCount > 0 && (
              <span className="ml-2 rounded-full bg-red-100 dark:bg-red-500/15 px-2 py-0.5 text-xs font-semibold text-red-700 dark:text-red-300">
                {criticalCount} critical
              </span>
            )}
          </p>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted"
          >
            <CheckSquare className="h-4 w-4 text-muted-foreground" weight="duotone" />
            Mark all read
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <MagnifyingGlass className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search notifications…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-400 focus:outline-none focus:ring-1 focus:ring-brand-400/30"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60 hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="mb-5 flex flex-wrap gap-2">
        {/* Unread toggle */}
        <Chip active={unreadOnly} onClick={() => setUnreadOnly((v) => !v)}>
          Unread only
        </Chip>

        {/* Category */}
        {(['hearings', 'comms', 'financial', 'tasks'] as const).map((cat) => (
          <Chip
            key={cat}
            active={category === cat}
            onClick={() => setCategory((v) => (v === cat ? '' : cat))}
          >
            {cat.charAt(0).toUpperCase() + cat.slice(1)}
          </Chip>
        ))}

        {/* Priority */}
        {(['critical', 'high'] as const).map((p) => (
          <Chip
            key={p}
            active={priorityFilter === p}
            onClick={() => setPriorityFilter((v) => (v === p ? '' : p))}
          >
            {PRIORITY_LABEL[p]}
          </Chip>
        ))}

        {/* Clear all */}
        {(unreadOnly || category || priorityFilter || search) && (
          <button
            onClick={() => {
              setUnreadOnly(false);
              setCategory('');
              setPriorityFilter('');
              setSearch('');
            }}
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-px">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[72px] animate-pulse rounded-lg bg-muted/40" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-xl border border-border bg-card">
          <QueryErrorState what="your notifications" onRetry={() => refetch()} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-border bg-card py-16 text-center">
          <Bell className="mx-auto mb-3 h-8 w-8 text-muted-foreground/30" />
          <p className="text-sm text-muted-foreground">No notifications match your filters.</p>
          {(unreadOnly || category || priorityFilter || search) && (
            <button
              onClick={() => {
                setUnreadOnly(false);
                setCategory('');
                setPriorityFilter('');
                setSearch('');
              }}
              className="mt-3 text-xs font-medium text-brand-600 dark:text-brand-400 hover:underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {filtered.map((n) => {
            const meta = ICON[n.type] ?? { Icon: Bell, tone: 'text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-500/15' };
            const { Icon: NIcon, tone } = meta;
            const isRead = Boolean(n.readAt);
            const isCritical = n.priority === 'critical';
            const isHigh = n.priority === 'high';
            return (
              <div
                key={n.id}
                className={cn(
                  'group flex items-start gap-4 border-b border-border/60 px-5 py-4 last:border-b-0',
                  isRead ? 'bg-card' : 'bg-brand-50/30',
                  isCritical && 'border-l-[3px] border-l-red-500',
                  isHigh && 'border-l-[3px] border-l-amber-500',
                )}
              >
                {/* Icon */}
                <div className={cn('mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', tone)}>
                  <NIcon className="h-4.5 w-4.5" weight="duotone" />
                </div>

                {/* Content */}
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p
                      className={cn(
                        'text-sm leading-snug',
                        isRead ? 'text-foreground' : 'font-semibold text-foreground',
                      )}
                    >
                      {n.title}
                    </p>
                    <span className="shrink-0 text-xs text-muted-foreground/70">
                      {relativeTime(n.createdAt)}
                    </span>
                  </div>
                  {n.body && (
                    <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                      {n.body}
                    </p>
                  )}
                  <div className="mt-1.5 flex items-center gap-2">
                    {!isRead && (
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-600" />
                    )}
                    {(isCritical || isHigh) && (
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                          isCritical ? 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300' : 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
                        )}
                      >
                        {PRIORITY_LABEL[n.priority]}
                      </span>
                    )}
                    <span className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground/50">
                      {n.type.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex shrink-0 items-center gap-1 opacity-0 transition group-hover:opacity-100">
                  <button
                    onClick={() => handleOpen(n)}
                    className="rounded px-2 py-1 text-xs font-medium text-brand-600 dark:text-brand-400 transition hover:bg-brand-50"
                  >
                    Open
                  </button>
                  <button
                    onClick={() => handleDismiss(n.id)}
                    className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground/50 transition hover:bg-border hover:text-foreground"
                    title="Dismiss"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Show count vs total */}
      {!isLoading && filtered.length > 0 && filtered.length < total && (
        <p className="mt-3 text-center text-xs text-muted-foreground">
          Showing {filtered.length} of {total} — adjust filters to narrow results.
        </p>
      )}
    </div>
  );
}
